import { useState, useEffect, useCallback } from 'react';

const LOCAL_STORAGE_KEY = 'formBuilder_elements';

export const useFormBuilder = (initialDarkMode) => {
  // State management
  const [darkMode, setDarkMode] = useState(() => {
    if (initialDarkMode !== undefined) return initialDarkMode;
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });

  const [formElements, setFormElements] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Failed to load form elements from localStorage:', error);
      return [];
    }
  });

  // Add formSettings to undo/redo tracking
  const [formSettings, setFormSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('formBuilder_settings');
      return saved ? JSON.parse(saved) : {
        title: '',
        description: '',
        theme: 'default',
        submitText: 'Submit',
        resetText: 'Reset',
        showProgress: false,
        allowDrafts: true,
        emailNotifications: false,
        redirectAfterSubmit: '',
        formWidth: 'medium',
        buttonStyle: 'contained',
      };
    } catch (error) {
      return {
        title: '',
        description: '',
        theme: 'default',
        submitText: 'Submit',
        resetText: 'Reset',
        showProgress: false,
        allowDrafts: true,
        emailNotifications: false,
        redirectAfterSubmit: '',
        formWidth: 'medium',
        buttonStyle: 'contained',
      };
    }
  });

  const [selectedElement, setSelectedElement] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Auto-save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formElements));
    } catch (error) {
      console.warn('Failed to save form elements to localStorage:', error);
    }
  }, [formElements]);

  // Undo/Redo functionality
  // Save both formElements and formSettings for undo
  const saveStateForUndo = useCallback(() => {
    setUndoStack((prev) => [
      ...prev.slice(-19),
      {
        formElements: JSON.parse(JSON.stringify(formElements)),
        formSettings: JSON.parse(JSON.stringify(formSettings)),
      },
    ]);
    setRedoStack([]);
  }, [formElements, formSettings]);

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setRedoStack((prev) => [
        {
          formElements: JSON.parse(JSON.stringify(formElements)),
          formSettings: JSON.parse(JSON.stringify(formSettings)),
        },
        ...prev,
      ]);
      setFormElements(previousState.formElements);
      setFormSettings(previousState.formSettings);
      setUndoStack((prev) => prev.slice(0, -1));
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[0];
      setUndoStack((prev) => [
        ...prev,
        {
          formElements: JSON.parse(JSON.stringify(formElements)),
          formSettings: JSON.parse(JSON.stringify(formSettings)),
        },
      ]);
      setFormElements(nextState.formElements);
      setFormSettings(nextState.formSettings);
      setRedoStack((prev) => prev.slice(1));
    }
  };

  // Element management
  const moveFormElement = useCallback((dragIndex, hoverIndex) => {
    setFormElements((prev) => {
      const updated = [...prev];
      const [removed] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, removed);
      return updated;
    });
  }, []);

  // Helper for form name and description
  const formName = formSettings.title || '';
  const setFormName = (name) => setFormSettings((prev) => ({ ...prev, title: name }));

  const formDescription = formSettings.description || '';
  const setFormDescription = (desc) => setFormSettings((prev) => ({ ...prev, description: desc }));

  return {
    // State
    darkMode,
    setDarkMode,
    formElements,
    setFormElements,
    formSettings,
    setFormSettings,
    formName,
    setFormName,
    formDescription,
    setFormDescription,
    selectedElement,
    setSelectedElement,
    undoStack,
    redoStack,

    // Actions
    handleUndo,
    handleRedo,
    moveFormElement,
    saveStateForUndo,
  };
};
